import { useState } from 'react'
import type { Color, Media } from '@nhanho/shared'
import { formatVND, computeSalePrice } from '@nhanho/shared'
import { Button } from '../../components/ui/Button'
import { Input, MoneyInput, Select } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { MediaPicker } from '../../components/media/MediaPicker'

export interface VariantRow {
  /** Khoá dòng trong bảng — **chỉ** dùng ở client. `id` của biến thể khi đã lưu, UUID khi mới thêm. */
  key: string
  id?: string
  sku: string
  /** Tên hiển thị cho khách. Tuỳ chọn — xem `ProductVariant.label`. */
  label: string
  color: string
  size: string
  price: number | null
  compareAtPrice: number | null
  stockQuantity: number | null
  image: Media | null
  active: boolean
}

export const newVariant = (basePrice: number | null): VariantRow => ({
  key: crypto.randomUUID(), sku: '', label: '', color: '', size: '', price: basePrice, compareAtPrice: null, stockQuantity: 0, image: null, active: true,
})

/** Ngưỡng tồn thấp — khớp `LOW_STOCK` ở `apps/api/src/routes/products.ts`. */
export const LOW_STOCK = 3

/**
 * Bảng biến thể: mỗi dòng = **một SKU** có tên, giá, tồn, ảnh riêng. Màu và kích cỡ **tuỳ chọn** —
 * nhiều sản phẩm không có cả hai, lúc đó cột "Tên" là thứ khách thấy để phân biệt.
 * Không có form lib — mỗi ô là input controlled ghi thẳng vào mảng `rows`.
 * `discountPercent` (nếu sản phẩm đang giảm giá) chỉ để **xem trước** giá sau giảm.
 */
export function VariantsEditor({ rows, onChange, colors, basePrice, errors = {}, discountPercent = null }: {
  rows: VariantRow[]; onChange: (rows: VariantRow[]) => void; colors: Color[]; basePrice: number | null
  errors?: Record<string, string>; discountPercent?: number | null
}) {
  const [pickFor, setPickFor] = useState<string | null>(null)
  /**
   * ⚠️ `key` rỗng là **chặn ngay**, không phải lọc cho vui: `undefined === undefined` đúng với mọi
   * dòng, nên một lần `update`/`remove` với key rỗng là ghi đè — hoặc xoá — **cả bảng**. Đúng lỗi
   * ngày 13/10 (API quên trả `id` cho biến thể). `ProductForm` đã tự vá key, đây là lớp thứ hai.
   */
  const update = (key: string, patch: Partial<VariantRow>) => { if (!key) return; onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r))) }
  const remove = (key: string) => { if (!key) return; onChange(rows.filter((r) => r.key !== key)) }
  const move = (i: number, dir: -1 | 1) => { const to = i + dir; if (to < 0 || to >= rows.length) return; const n = [...rows]; const [it] = n.splice(i, 1); n.splice(to, 0, it!); onChange(n) }

  const dupSkus = new Set(rows.map((r) => r.sku.trim().toUpperCase()).filter((s, i, a) => s && a.indexOf(s) !== i))
  const sale = (price: number | null) => (discountPercent && price != null ? computeSalePrice(price, discountPercent) : null)

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-xs text-ink-muted">Mỗi biến thể có SKU, giá và tồn kho riêng. <strong className="font-medium text-ink">Màu và Size đều tuỳ chọn</strong> — không có thì để trống, khách sẽ chọn theo cột Tên. Không có biến thể nào → dùng giá/tồn ở tab Cơ bản.</p>
        <Button variant="secondary" size="sm" onClick={() => onChange([...rows, newVariant(basePrice)])}>+ Thêm dòng</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-card-sm border border-dashed border-border-strong p-6 text-center text-sm text-ink-muted">Chưa có biến thể.</div>
      ) : (
        <div className="overflow-x-auto rounded-card-sm border border-border">
          <table className="w-full text-sm">
            <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-2 py-2">Ảnh</th><th className="px-2 py-2">Tên biến thể</th><th className="px-2 py-2">SKU *</th><th className="px-2 py-2">Màu</th><th className="px-2 py-2">Size</th><th className="px-2 py-2">Giá *</th><th className="px-2 py-2">Giá gốc</th><th className="px-2 py-2">Tồn</th><th className="px-2 py-2">Bật</th><th className="px-2 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => {
                const skuErr = dupSkus.has(r.sku.trim().toUpperCase()) ? 'SKU trùng' : errors[`variants.${i}.sku`]
                const cmpErr = r.compareAtPrice != null && r.price != null && r.compareAtPrice <= r.price ? 'Phải lớn hơn giá bán' : errors[`variants.${i}.compareAtPrice`]
                const low = r.active && r.stockQuantity != null && r.stockQuantity <= LOW_STOCK
                const s = sale(r.price)
                return (
                  <tr key={r.key} className={r.active ? '' : 'opacity-60'}>
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => setPickFor(r.key)} className="size-10 overflow-hidden rounded-image border border-border bg-sand" title="Chọn ảnh riêng cho biến thể">
                        {r.image ? <img src={r.image.sizes?.thumbnail?.url ?? r.image.url} alt="" className="size-full object-cover" /> : <span className="text-xs text-ink-faint">+</span>}
                      </button>
                    </td>
                    {/* Nhãn khách thấy trên web. Để trống thì web ghép từ màu + size, không có cả hai thì hiện SKU. */}
                    <td className="px-2 py-1.5 min-w-40"><Input value={r.label} onChange={(e) => update(r.key, { label: e.target.value })} placeholder="Combo nơ tím" /></td>
                    <td className="px-2 py-1.5 min-w-32"><Input value={r.sku} onChange={(e) => update(r.key, { sku: e.target.value.toUpperCase() })} className="font-mono uppercase" aria-invalid={!!skuErr} placeholder="CAO-HONG-S" />{skuErr && <div className="mt-0.5 text-xs text-danger">{skuErr}</div>}</td>
                    <td className="px-2 py-1.5 min-w-32">
                      <Select value={r.color} onChange={(e) => update(r.key, { color: e.target.value })}><option value="">—</option>{colors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
                    </td>
                    <td className="px-2 py-1.5 w-24"><Input value={r.size} onChange={(e) => update(r.key, { size: e.target.value })} placeholder="M" /></td>
                    <td className="px-2 py-1.5 min-w-32">
                      <MoneyInput value={r.price} onChange={(v) => update(r.key, { price: v })} aria-invalid={!!errors[`variants.${i}.price`]} />
                      {errors[`variants.${i}.price`] && <div className="mt-0.5 text-xs text-danger">{errors[`variants.${i}.price`]}</div>}
                      {s != null && <div className="mt-0.5 text-xs text-warning">→ {formatVND(s)} (−{discountPercent}%)</div>}
                    </td>
                    <td className="px-2 py-1.5 min-w-32"><MoneyInput value={r.compareAtPrice} onChange={(v) => update(r.key, { compareAtPrice: v })} aria-invalid={!!cmpErr} />{cmpErr && <div className="mt-0.5 text-xs text-danger">{cmpErr}</div>}</td>
                    <td className="px-2 py-1.5 w-24">
                      <Input type="number" min={0} value={r.stockQuantity ?? ''} aria-invalid={low} onChange={(e) => update(r.key, { stockQuantity: e.target.value === '' ? null : Math.max(0, Math.floor(Number(e.target.value))) })} />
                      {low && <div className="mt-0.5 text-xs text-warning">Tồn thấp</div>}
                    </td>
                    <td className="px-2 py-1.5"><Toggle checked={r.active} onChange={(v) => update(r.key, { active: v })} /></td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Lên">↑</Button>
                      <Button variant="ghost" size="sm" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label="Xuống">↓</Button>
                      <Button variant="ghost" size="sm" className="text-danger" onClick={() => remove(r.key)} aria-label="Xoá dòng">✕</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          Giá hiển thị trên web: {(() => { const ps = rows.filter((r) => r.active && r.price != null).map((r) => sale(r.price) ?? r.price!); if (!ps.length) return '—'; const a = Math.min(...ps), b = Math.max(...ps); return a === b ? formatVND(a) : `từ ${formatVND(a)} đến ${formatVND(b)}` })()}
          {discountPercent ? ` (đã áp giảm ${discountPercent}%)` : ''}
        </p>
      )}
      <MediaPicker open={!!pickFor} onClose={() => setPickFor(null)} onPick={(d) => pickFor && update(pickFor, { image: d[0] ?? null })} />
    </div>
  )
}
